import { Modal } from "react-bootstrap";
import { Visitor } from "./visitor";
import Avvvatars from "avvvatars-react";
import moment from "moment";
import { LEVEL_OF_INTEREST } from "./VisitorTable";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

interface Props {
  show: boolean;
  visitor: Visitor | null;
  filiere: { label: string; id: string };
  onClose: () => void;
}

const VisitorDetailsModal = ({ show, visitor, filiere, onClose }: Props) => {
  if (!visitor) return null;

  const matchedLevelOfInterest = (label: string) => {
    const level = LEVEL_OF_INTEREST.find((item) => item.label === label);
    return level;
  };

  const infoItem = (
    icon: string,
    label: string,
    value: string | undefined,
    color: string,
  ) => (
    <div className="info-card d-flex align-items-center p-3 mb-3">
      <div className={`info-icon bg-${color}-transparent text-${color}`}>
        <i className={`ti ti-${icon}`}></i>
      </div>

      <div>
        <p className="info-label">{label}</p>
        <h6 className="info-value">{value || "Non spécifié"}</h6>
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="visitor-details-modal"
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title>Détails du Visiteur</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="visitor-header">
          <div className="avatar-wrapper d-flex flex-column justify-content-center align-items-center">
            <Avvvatars
              value={`${visitor.firstname?.charAt(0) || ""}${visitor.lastname?.charAt(0) || ""}`}
              style="character"
              size={100}
            />

            <span
              className={`status-badge ${visitor.registration_statut ? "success" : "pending"}`}
            >
              {visitor.registration_statut ? "Inscrit" : "En attente"}
            </span>
          </div>

          <h4>
            {visitor.firstname} {visitor.lastname}
          </h4>

          <p className="email">
            <i className="ti ti-mail"></i>
            {visitor.email || "Pas d'email"}
          </p>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <h6 className="section-title">Informations de Contact</h6>

            {infoItem(
              "phone",
              "Numéro de téléphone",
              visitor.number,
              "primary",
            )}
            {infoItem(
              "map-pin",
              "Établissement d'origine",
              visitor.establishment_of_origin,
              "info",
            )}
          </div>

          <div className="col-md-6">
            <h6 className="section-title">Parcours Académique</h6>

            {infoItem(
              "school",
              "Niveau d'étude actuel",
              visitor.level_of_study,
              "success",
            )}
            {infoItem(
              "book",
              "Filière / Domaine",
              visitor.field_of_study === "Autre"
                ? visitor.otherField_Of_Study
                : visitor.field_of_study || "Non spécifié",
              "secondary",
            )}
          </div>

          <div className="col-md-6">
            <h6 className="section-title">Intérêts et Projets</h6>

            {infoItem(
              "direction-arrows",
              "Niveau souhaité",
              visitor.level_desired === "Autre"
                ? visitor.otherLevel_Of_Study
                : visitor.level_desired || "Non spécifié",
              "warning",
            )}

            {infoItem(
              "books",
              "Filière souhaitée",
              visitor.id_filiere ? filiere?.label : "Non spécifié",
              "danger",
            )}
            <div className="info-card p-3">
              <p className="info-label">Degré d'intérêt</p>

              <div className="progress progress-modern">
                <div
                  className={`progress-bar ${matchedLevelOfInterest(visitor.level_of_interest)?.color || "bg-info"}`}
                  style={{
                    width:
                      matchedLevelOfInterest(visitor.level_of_interest)
                        ?.value || "00%",
                  }}
                ></div>
              </div>

              <h6 className="info-value mt-2">{visitor.level_of_interest}</h6>
            </div>
          </div>

          <div className="col-md-6">
            <h6 className="section-title">Historique</h6>

            {infoItem(
              "calendar",
              "Date d'enregistrement",
              visitor.createdAt
                ? moment(visitor.createdAt).format("LLLL")
                : moment().format("LLLL"),
              "purple",
            )}

            <div className="info-card p-3 h-90">
              <p className="info-label">Observations</p>

              <p className="observation" style={{ wordWrap: "break-word" }}>
                {visitor.observation ||
                  "Aucune observation particulière pour ce visiteur."}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="modal-footer-custom">
        <button className="btn btn-cancel" onClick={onClose}>
          Fermer
        </button>
      </Modal.Footer>

      <style>{`

/* Modal container */

.visitor-details-modal .modal-content{
border-radius:16px;
border:1px solid rgba(0,0,0,0.06);
box-shadow:
0 10px 25px rgba(0,0,0,0.05),
0 4px 10px rgba(0,0,0,0.04);
padding:8px;
}


/* Header */

.modal-header-custom{
border:none;
padding:20px 24px 10px 24px;
}

.modal-header-custom .modal-title{
font-size:18px;
font-weight:600;
}


/* Body */

.visitor-details-modal .modal-body{
padding:20px 24px;
}


/* Footer */

.modal-footer-custom{
border:none;
padding:16px 24px 20px;
display:flex;
justify-content:flex-end;
gap:10px;
}


/* Visitor header */

.visitor-header{
text-align:center;
margin-bottom:25px;
}

.visitor-header h4{
font-size:18px;
font-weight:600;
margin-top:12px;
}

.email{
color:#6b7280;
font-size:13px;
display:flex;
justify-content:center;
gap:6px;
}


/* Avatar */

.avatar-wrapper{
position:relative;
display:inline-block;
}

.status-badge{
position:absolute;
bottom:-5px;
right:-5px;
font-size:11px;
padding:4px 10px;
border-radius:999px;
color:white;
}

.status-badge.success{
background:#16a34a;
}

.status-badge.pending{
background:#f59e0b;
}


/* Section title */

.section-title{
font-size:14px;
font-weight:600;
margin-bottom:12px;
}


/* Info cards */

.info-card{
background:#f8f8f8;
border-radius:12px;
border:1px solid rgba(0,0,0,0.05);
transition:all .2s;
}

.info-card:hover{
background:#f3f3f3;
}


/* Icon */

.info-icon{
width:40px;
height:40px;
border-radius:10px;
display:flex;
align-items:center;
justify-content:center;
margin-right:12px;
font-size:18px;
}


/* Labels */

.info-label{
font-size:12px;
color:#6b7280;
margin-bottom:2px;
}

.info-value{
font-size:14px;
font-weight:600;
margin:0;
}


/* Observation */

.observation{
font-size:13px;
color:#374151;
font-style:italic;
}


/* Buttons */

.btn-confirm{
background:#111;
color:white;
border:none;
border-radius:10px;
padding:8px 16px;
display:flex;
align-items:center;
gap:6px;
}

.btn-confirm:hover{
background:black;
}

.btn-cancel{
// background:#f3f3f3;
background:red;
color:white;
border:none;
border-radius:10px;
padding:8px 16px;
}

.btn-cancel:hover{
color:red;
}

/* Progress */

.progress-modern{
height:8px;
background:#e5e7eb;
border-radius:10px;
}

.progress-modern .progress-bar{
border-radius:10px;
}


/* Transparent colors */

.bg-primary-transparent{background:rgba(13,110,253,0.1);}
.bg-success-transparent{background:rgba(25,135,84,0.1);}
.bg-info-transparent{background:rgba(13,202,240,0.1);}
.bg-warning-transparent{background:rgba(255,193,7,0.1);}
.bg-danger-transparent{background:rgba(220,53,69,0.1);}
.bg-secondary-transparent{background:rgba(108,117,125,0.1);}
.bg-purple-transparent{background:rgba(111,66,193,0.1);}

.text-purple{color:#6f42c1;}

`}</style>
    </Modal>
  );
};

export default withPermission(
  VisitorDetailsModal,
  [
    buildPermission(Resource.VISITOR, Action.READ),
    buildPermission(Resource.VISITOR, Action.CREATE),
    buildPermission(Resource.VISITOR, Action.UPDATE),
    buildPermission(Resource.VISITOR, Action.DELETE),
  ],
  "masquer",
);
